/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Keycloak authentication provider configuration
 *
 * Defines the Keycloak Angular environment providers used only when the build
 * selects the Keycloak authentication provider.
 */

import {EnvironmentProviders} from '@angular/core';
import {provideKeycloak} from 'keycloak-angular';
import type {KeycloakInitOptions} from 'keycloak-js';

import {environment} from '../../../environments/environment';
import type {IKeycloakConfiguration} from './keycloak-configuration';

/** OIDC scopes requested from Keycloak for the web application session. */
const KEYCLOAK_SCOPE = 'openid profile email';

/**
 * Application-relative path of the static page loaded in the hidden iframe
 * that completes the silent `check-sso` session restoration.
 */
export const SILENT_CHECK_SSO_PATH = '/assets/silent-check-sso.html';

/**
 * Gets the Keycloak configuration from an environment that supports Keycloak.
 *
 * @returns The configured Keycloak client settings.
 * @throws Error when the selected environment has no Keycloak configuration.
 */
export function getKeycloakConfiguration(): IKeycloakConfiguration {
  const keycloakConfiguration = environment.keycloak;

  if (!keycloakConfiguration) {
    throw new Error(
      'Keycloak authentication is not configured for the selected environment.',
    );
  }

  return keycloakConfiguration;
}

/**
 * Creates the Keycloak adapter initialization options.
 *
 * `check-sso` restores an existing Keycloak session without an interactive
 * redirect. The silent check runs in a hidden iframe that loads
 * {@link SILENT_CHECK_SSO_PATH}, so the browser stays on the requested deep
 * link during a cold load instead of being redirected to a fixed route. The
 * legacy login-status iframe is disabled because third-party cookie
 * restrictions make it unreliable; the authentication provider refreshes
 * access tokens on demand instead.
 *
 * @returns The `keycloak-js` initialization options.
 */
export function createKeycloakInitOptions(): KeycloakInitOptions {
  return {
    onLoad: 'check-sso',
    pkceMethod: 'S256',
    silentCheckSsoRedirectUri: `${window.location.origin}${SILENT_CHECK_SSO_PATH}`,
    checkLoginIframe: false,
    scope: KEYCLOAK_SCOPE,
  };
}

/**
 * Creates the Keycloak environment providers for the NgModule application.
 *
 * `provideKeycloak()` returns `EnvironmentProviders`, which `NgModule.providers`
 * accepts, so the application keeps its module-based bootstrap. The Keycloak
 * bearer interceptor is intentionally not registered: `CustomAuthHttpInterceptor`
 * adds tokens to protected backend requests for every authentication provider.
 *
 * @returns The Keycloak client, event signal, and initializer providers.
 * @throws Error when the selected environment has no Keycloak configuration.
 */
export function provideKeycloakAuthentication(): EnvironmentProviders {
  const {url, realm, clientId} = getKeycloakConfiguration();

  return provideKeycloak({
    config: {url, realm, clientId},
    initOptions: createKeycloakInitOptions(),
  });
}
